// partnerships/partnershipsStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { partnershipsService } from '../services/partnerships.service';

// Structured deliverable
export interface Deliverable {
  contentType: string;
  title: string;
  description?: string;
  deadline?: string;
}

export interface Partnership {
  id: number;
  brand: string;
  contact: string;
  email: string;
  stage: 'Lead' | 'Pitch Sent' | 'Contract Sent' | 'Content Delivered' | 'Payment Received';
  value: number;
  contentType?: string;
  deliveryDate?: string;
  status?: string;
  notes?: string;
  tags?: string[];
  priority?: 'low' | 'medium' | 'high';
  lastUpdated?: string;
  createdAt?: string;
  phone?: string;
  website?: string;
  socialMedia?: {
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    tiktok?: string;
  };
  campaignType?: string;
  campaignDescription?: string;
  deliverables?: Deliverable[];
}

export interface CreatePartnershipData {
  brand: string;
  contact: string;
  email: string;
  stage: Partnership['stage'];
  value: number;
  contentType?: string;
  deliveryDate?: string | undefined;
  notes?: string;
  tags?: string[];
  priority?: Partnership['priority'];
  phone?: string;
  website?: string;
  socialMedia?: Partnership['socialMedia'];
  campaignType?: string;
  campaignDescription?: string;
  deliverables?: Deliverable[];
}

export interface UpdatePartnershipData extends Partial<CreatePartnershipData> {
  id: number;
}

interface PartnershipsState {
  // State
  partnerships: Partnership[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  filterStage: string;
  sortBy: 'brand' | 'value' | 'stage' | 'lastUpdated';
  sortOrder: 'asc' | 'desc';

  // Actions
  setSearchTerm: (term: string) => void;
  setFilterStage: (stage: string) => void;
  setSortBy: (sort: PartnershipsState['sortBy']) => void;
  setSortOrder: (order: PartnershipsState['sortOrder']) => void;

  // Async Actions
  fetchPartnerships: () => Promise<void>;
  createPartnership: (data: CreatePartnershipData) => Promise<Partnership | null>;
  updatePartnership: (data: UpdatePartnershipData) => Promise<Partnership | null>;
  deletePartnership: (id: number) => Promise<boolean>;

  // Computed getters
  getFilteredPartnerships: () => Partnership[];
  getTotalValue: () => number;
  getCompletedValue: () => number;
  getPendingValue: () => number;
  getPartnershipsByStage: () => Record<string, Partnership[]>;
}

export const usePartnershipsStore = create<PartnershipsState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        partnerships: [],
        isLoading: false,
        error: null,
        searchTerm: '',
        filterStage: 'all',
        sortBy: 'lastUpdated',
        sortOrder: 'desc',

        // Filter actions
        setSearchTerm: (term) => set({ searchTerm: term }),
        setFilterStage: (stage) => set({ filterStage: stage }),
        setSortBy: (sort) => set({ sortBy: sort }),
        setSortOrder: (order) => set({ sortOrder: order }),

        // Fetch partnerships
        fetchPartnerships: async () => {
          set({ isLoading: true, error: null });
          try {
            const partnerships = await partnershipsService.getAll();
            set({
              partnerships,
              isLoading: false,
              error: null
            });
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch partnerships'
            });
          }
        },

        // Create partnership
        createPartnership: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const newPartnership = await partnershipsService.create(data);
            set((state) => ({
              partnerships: [...state.partnerships, newPartnership],
              isLoading: false,
              error: null
            }));
            return newPartnership;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to create partnership'
            });
            return null;
          }
        },

        // Update partnership
        updatePartnership: async (data) => {
          set({ isLoading: true, error: null });
          try {
            // Extract id and remove it from the update payload
            const { id, ...updatePayload } = data;
            const updatedPartnership = await partnershipsService.update(id, updatePayload);
            set((state) => ({
              partnerships: state.partnerships.map(p =>
                p.id === id ? updatedPartnership : p
              ),
              isLoading: false,
              error: null
            }));
            return updatedPartnership;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update partnership'
            });
            return null;
          }
        },

        // Delete partnership
        deletePartnership: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await partnershipsService.delete(id);
            set((state) => ({
              partnerships: state.partnerships.filter(p => p.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete partnership'
            });
            return false;
          }
        },

        // Computed getters
        getFilteredPartnerships: () => {
          const { partnerships, searchTerm, filterStage, sortBy, sortOrder } = get();

          let filtered = partnerships.filter(partnership => {
            const matchesSearch = !searchTerm ||
              partnership.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
              partnership.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
              partnership.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (partnership.tags && partnership.tags.some(tag =>
                tag.toLowerCase().includes(searchTerm.toLowerCase())
              ));

            const matchesStage = filterStage === 'all' || partnership.stage === filterStage;

            return matchesSearch && matchesStage;
          });

          // Sort results
          filtered.sort((a, b) => {
            let aVal: any = a[sortBy];
            let bVal: any = b[sortBy];

            if (sortBy === 'value') {
              aVal = Number(aVal);
              bVal = Number(bVal);
            } else if (sortBy === 'lastUpdated') {
              aVal = new Date(aVal || a.createdAt || 0).getTime();
              bVal = new Date(bVal || b.createdAt || 0).getTime();
            } else {
              aVal = String(aVal || '').toLowerCase();
              bVal = String(bVal || '').toLowerCase();
            }

            if (sortOrder === 'asc') {
              return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            } else {
              return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
            }
          });

          return filtered;
        },

        getTotalValue: () => {
          return get().partnerships.reduce((sum, partnership) => sum + partnership.value, 0);
        },

        getCompletedValue: () => {
          return get().partnerships
            .filter(p => p.stage === 'Payment Received')
            .reduce((sum, partnership) => sum + partnership.value, 0);
        },

        getPendingValue: () => {
          return get().partnerships
            .filter(p => p.stage !== 'Payment Received')
            .reduce((sum, partnership) => sum + partnership.value, 0);
        },

        getPartnershipsByStage: () => {
          const partnerships = get().partnerships;
          return partnerships.reduce((acc, partnership) => {
            const stage = partnership.stage;
            if (!acc[stage]) {
              acc[stage] = [];
            }
            acc[stage].push(partnership);
            return acc;
          }, {} as Record<string, Partnership[]>);
        },
      }),
      {
        name: 'partnerships-storage',
        partialize: (state) => ({
          partnerships: state.partnerships,
          searchTerm: state.searchTerm,
          filterStage: state.filterStage,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }),
      }
    ),
    {
      name: 'partnerships-store',
    }
  )
);